<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import moment from "moment-timezone";

import { ModalName } from '@/constants/modals';
import { formatDate, getStartMissionDate } from "@/helpers/date";
import { getMissionStatusInfo } from "@/helpers/mission";
import { isset } from "@/helpers/object";
import { isMissionExtended, isMissionSyncWithEvolia } from "@/modules/companyNeed/helpers/mission";
import { useConfigurationStore } from "@/store/configurationStore";
import { useMissionActionsStore } from "@/store/missionActionsStore";
import { type IMissionList, type MissionCommand, type MissionStatus, MissionStatusGroup } from '@/types/mission';

import type { KanbanColumnIconColor } from "@/dsComponents/kanban/types";
import type { LabelValue } from '@/types/common';

import Button from "@/dsComponents/buttons/Button.vue";
import ComboButton from "@/dsComponents/buttons/ComboButton.vue";
import Link from "@/dsComponents/buttons/Link.vue";
import Dropdown from "@/dsComponents/Dropdown/Dropdown.vue";
import DropdownGenerator from "@/dsComponents/Dropdown/DropdownGenerator.vue";
import { DropdownDirections, type DropdownGeneratorProps } from "@/dsComponents/Dropdown/types";
import { useModalStore } from "@/dsComponents/modals/modalStore";
import SideModal2 from "@/dsComponents/modals/SideModal2.vue";
import Separator from "@/dsComponents/Separator.vue";
import TableAlt from "@/dsComponents/TableAlt/TableAlt.vue";

import DateFormated from "@/components/dateFormatted/DateFormated.vue";
import HourFormated from "@/components/Mission/HourFormated.vue";
import MissionLabels from "@/components/Mission/MissionLabels.vue";
import TableDropdown from "@/components/tableDropdowns/TableDropdown.vue";

interface MissionDetailsModalProps {
    mission: IMissionList;
    open: boolean;
    hideFooter?: boolean;
    noActions?: boolean;
}

const DATE_FORMAT = "ddd DD MMM YY";

const props = defineProps<MissionDetailsModalProps>();
const emit = defineEmits<(e: "close") => void>();

const { t } = useI18n();

const missionActionsStore = useMissionActionsStore();
const configStore = useConfigurationStore();

const isSeeMoreSectionOpen = ref<boolean>(false);
interface AsideItems {
    label: string;
    icon?: string;
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    value: any;
    iconColor?: keyof typeof KanbanColumnIconColor;
    valueClass?: string;
}

const hiringReasons = ref<LabelValue[]>([]);

const actions = ref<DropdownGeneratorProps>({
    actions: [],
});

const modalStore = useModalStore();

const columnsDateTable = [{ field: "day", width: "220px" }, { field: "hour" }];

const toggleSeeMoreSection = () => {
    isSeeMoreSectionOpen.value = !isSeeMoreSectionOpen.value;
};

const getPrincipalInfo = (mission: IMissionList): AsideItems[] => {
    const values: AsideItems[] = [];

    if (!mission) return [];

    if (mission.ownerAgency && mission.createdAt) {
        values.push({
            label: t("page.worker.applications.tableHeaders.createdAt"),
			// biome-ignore lint/style/useTemplate: Init Biome
            value: formatDate(mission.createdAt, DATE_FORMAT) + " - " + ` ${mission.ownerAgency.firstname} ${mission.ownerAgency.lastname}`,
            valueClass: "created_at_custom",
        });
    }

    if (mission.status) {
        values.push({
            label: t("page.worker.applications.tableHeaders.status"),
            icon: getMissionStatusInfo(mission.status).icon,
            iconColor: getMissionStatusInfo(mission.status).color as keyof typeof KanbanColumnIconColor,
            value: getMissionStatusInfo(mission.status).statusKanban,
        });
    }
    if (mission.client) {
        values.push({
            label: t("form.company"),
            icon: "company",
            value: mission.client.name,
        });
    }
    if (mission.address) {
        values.push({
            label: t("form.location"),
            icon: "map-pin-circle",
            value: mission.address.city,
        });
    }
    if (mission.positions[0].contractType) {
        values.push({
            label: t("page.order.contract"),
            icon: "signature",
            value: mission.positions[0].contractType,
        });
    }
    if (mission.positions[0].job.name) {
        values.push({
            label: t("form.post"),
            icon: "work",
            value: mission.positions[0].job.name,
        });
    }
    if (mission.positions[0].quantity) {
        values.push({
            label: t("form.postQuantity"),
            icon: "users-group",
            value: mission.positions[0].quantity.toString(),
        });
    }

    return values;
};

const getSupplementInfo = (mission: IMissionList): AsideItems[] => {
    const values: AsideItems[] = [];
    if (mission.positions && mission.positions.length > 0) {
        if (mission.positions[0].experience) {
            values.push({
                label: t("form.experience"),
                icon: "level-3",
                value: t(`jobExperience.${mission.positions[0].experience}`),
            });
        }
        if (mission.positions[0].hourlyWage) {
            values.push({
                label: t("page.order.hourlyWage"),
                icon: "cash",
			    // biome-ignore lint/style/useTemplate: Init Biome
                value: (mission.positions[0].hourlyWage / 100).toFixed(2).replace(".", ",") + " €",
            });
        }
        if (mission.positions[0].hiringReason) {
            values.push({
                label: t("global.motif"),
                icon: "list-check",
                value: getHiringReason(mission.positions[0].hiringReason) ?? "Aucune",
            });
        }
    }
    return values;
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const getFormattedDate = (mission: any) => {
    const periods = mission.periods;
    if (!periods.length) return [];

    const formatTime = (time: number): string => {
        const strTime = time.toString().padStart(4, "0");
        return `${strTime.slice(0, 2)}:${strTime.slice(2)}`;
    };

    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    return periods.map((period: any) => ({
        content: {
            day: {
                customElement: {
                    component: DateFormated,
                    props: {
                        startDate: formatDate(period.start, DATE_FORMAT),
                        endDate: formatDate(period.end, DATE_FORMAT),
                        icon: "arrow-right",
                    },
                },
            },
            hour:
                period.slots?.all?.length > 1
                    ? getPeriodDropdown(period.slots?.all)
                    : {
                          customElement: {
                              component: HourFormated,
                              props: {
                                  from: formatTime(Number(period.slots.all[0]?.from) || 0),
                                  to: formatTime(Number(period.slots.all[0]?.to) || 0),
                                  icon: "arrow-right",
                              },
                          },
                      },
        },
        editMode: false,
    }));
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const getPeriodDropdown = (data: any[]) => {
    return {
        customElement: {
            component: TableDropdown,
            props: {
                trigger: "Plusieurs créneaux",
                width: "150px",
                mode: "hover",
                direction: DropdownDirections.LEFT,
                items: data.map((slot) => ({
                    component: HourFormated,
                    componentProps: {
                        from: slot.from,
                        to: slot.to,
                        icon: "arrow-right",
                    },
                })),
            },
        },
    };
};

const formattedDates = computed(() => {
    if (!props.mission) return [];
    return getFormattedDate(props.mission);
});

const iconBtnMoreInfo = computed(() => (isSeeMoreSectionOpen.value ? "caret-down-filled" : "caret-right-filled"));

const getHiringReason = (code: string) => {
    return hiringReasons.value.find((reason: LabelValue) => reason.value === code)?.label ?? "Aucun";
};

const setActions = () => {
    actions.value = {
        relatedData: { missionId: props.mission.id, mission: props.mission },
        actions: missionActionsStore.getMissionActions(props.mission.availableCommands as MissionCommand[]),
    };
};

const handleClose = () => {
    modalStore.actions.close(ModalName.MissionDetails);
    emit("close");
};

onBeforeMount(async () => {
    hiringReasons.value = configStore.hiringReasonOptions;
    setActions();
});

const isExpired = computed(() => {
    if (!MissionStatusGroup.unclosedStatus.includes(props.mission.status as MissionStatus)) {
        return false;
    }
    const startDate = getStartMissionDate(props.mission.periods);
    return moment().diff(moment(startDate), "days") > 1;
});

const jobOffer = computed(() => {
    return { id: props.mission.jobOfferId || "", workerToTreated: props.mission.jobOffer?.workerToTreated || 0 };
});

const isSyncWithEvolia = computed(() => isMissionSyncWithEvolia(props.mission.sendToEvoliaTimestamp));

const isExtended = computed(() => isMissionExtended(props.mission.extendsOf));

watch(
    () => props.open,
    (newVal) => {
        if (isset(newVal)) {
            setActions();
        }
    }
);
</script>
<template>
    <SideModal2 :title="mission?.name ?? ''" :width="420" :open="open" :close="false" @close="handleClose">
        <template #header-actions>
            <MissionLabels :isExpired="isExpired" :jobOffer="jobOffer" :isSyncWithEvolia="isSyncWithEvolia" :isExtended="isExtended" />
            <Dropdown width="200px" close-on-descendant v-if="!noActions">
                <template #trigger-zone>
                    <Button iconOnly="dots" size="xs" type="neutral" variant="ghost" />
                </template>
                <template #dropdown-content>
                    <DropdownGenerator v-bind="actions" />
                </template>
            </Dropdown>
        </template>
        <div class="content-modal">
            <div class="mission-infos">
                <div class="principal-info">
                    <div class="aside-section-form">
                        <div v-for="(item, index) in getPrincipalInfo(mission)" :key="index" class="aside-items">
                            <div class="side-modal-label">{{ item.label }}</div>
                            <div class="side-modal-value">
                                <ComboButton
                                    class="label-combo"
                                    :noInnerPadding="!item.icon"
                                    variant="ghost"
                                    :icon-left="item.icon"
                                    :iconLeftColor="item.iconColor"
                                    readonly
                                >
                                    <span :class="item.valueClass">
                                        {{ item.value }}
                                    </span>
                                </ComboButton>
                            </div>
                        </div>
                    </div>
                </div>

                <Link
                    v-if="getSupplementInfo(mission).length > 0"
                    class="link"
                    :rightIcon="iconBtnMoreInfo"
                    :label="t('global.more')"
                    type="neutral"
                    @click="toggleSeeMoreSection"
                    size="xs"
                />

                <div v-if="isSeeMoreSectionOpen" class="supplement-info">
                    <div class="aside-section-form">
                        <div v-for="(item, index) in getSupplementInfo(mission)" :key="index" class="aside-items">
                            <div class="side-modal-label">{{ item.label }}</div>
                            <div class="side-modal-value">
                                <ComboButton class="label-combo" variant="ghost" :icon-left="item.icon" readonly>{{ item.value }}</ComboButton>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Separator />
            <div class="mission-date">
                <h2>{{ t("mission.dates") }}</h2>
                <div class="dates-table">
                    <TableAlt :columns="columnsDateTable" :rows="formattedDates" />
                </div>
            </div>
            <Separator />
            <div class="mission-describe">
                <h2>{{ t("mission.description") }}</h2>
                <div class="describe-content">
                    <pre>{{ mission?.description ?? "-" }}</pre>
                    <div v-if="mission?.internalAgencyNotes" class="note">
                        <span>{{ t("mission.warningNotes") }}</span>
                        <pre>{{ mission?.internalAgencyNotes }}</pre>
                    </div>
                </div>
            </div>
        </div>
    </SideModal2>
</template>
<style scoped lang="scss">
.content-modal {
    gap: 24px;
    display: flex;
    padding: 24px 0;
    flex-direction: column;

    .aside-section-form {
        gap: 8px;
        display: flex;
        flex-direction: column;

        .aside-items {
            padding-left: 12px;
            height: 28px;
            display: flex;
            align-items: center;

            .side-modal-label {
                flex-shrink: 0;
                display: flex;
                align-items: center;
                width: 100px;
                height: 28px;
                color: $text-sub-600;
                @include font-size-12;
            }

            .side-modal-value {
                height: 28px;
                display: flex;
                align-items: center;
                width: 100%;
                overflow: hidden;
            }
        }
    }

    .mission-infos {
        padding: 0 12px;
        display: flex;
        flex-direction: column;
        gap: 16px;
        .link {
            padding-left: 8px;
        }

        .created_at_custom {
            color: $neutral-600;
            font-weight: 400;
            @include font-size-12;
        }
    }

    .mission-date {
        padding: 0 6px;
        display: flex;
        flex-direction: column;
        gap: 16px;

        h2 {
            padding: 0 18px;
            font-weight: 500;
            @include font-size-14;
            color: $neutral-700;
        }

        .dates-table {
            padding: 0 6px;
        }
    }

    .mission-describe {
        gap: 16px;
        display: flex;
        padding: 0 12px;
        flex-direction: column;

        h2 {
            @include font-size-14;
            font-weight: 500;
            padding: 0 12px;
            color: $neutral-700;
        }
        .describe-content {
            padding: 0 12px;
            pre {
                @include font-size-13;
                font-weight: 500;
                white-space: pre-wrap;
                word-wrap: break-word;
                overflow-wrap: break-word;
                max-width: 100%;
                margin-bottom: 30px;
            }
            .note {
                display: flex;
                flex-direction: column;
                gap: 4px;
                span {
                    font-weight: 500;
                    @include font-size-12;
                    color: $neutral-600;
                }
                pre {
                    font-weight: 500;
                    @include font-size-13;
                    white-space: pre-wrap;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                    max-width: 100%;
                    margin: 0;
                }
            }
        }
    }
}

.footer {
    display: flex;
    width: 100%;
    gap: 12px;
}
</style>
